import type { SxProps, Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";

export const root: SxProps<Theme> = (theme) => ({
  position: "sticky",
  top: 0,
  zIndex: theme.zIndex.appBar,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: { xs: 1, sm: 1.5, md: 2 },
  minHeight: { xs: 52, sm: 56, md: 64 },
  px: { xs: 1.5, sm: 2, md: 4 },
  bgcolor: "background.paper",
  borderBottom: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[1],
  boxSizing: "border-box",
  width: "100%",
});

export const startCluster: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: { xs: 1, sm: 1.25, md: 2, lg: 3.25 },
  minWidth: 0,
  flex: 1,
  overflow: "hidden",
};

export const endCluster: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: { xs: 0.75, sm: 1, md: 1.75 },
  flexShrink: 0,
};

export const logo: SxProps<Theme> = {
  height: { xs: 22, sm: 24, md: 26 },
  width: "auto",
  objectFit: "contain",
  flexShrink: 0,
};

export const nav: SxProps<Theme> = {
  display: { xs: "none", md: "flex" },
  alignItems: "center",
  gap: { md: 0.25, lg: 0.5 },
  minWidth: 0,
  flexShrink: 1,
  overflow: "hidden",
};

export const navLink =
  (active: boolean): SxProps<Theme> =>
  (theme) => ({
    px: { md: 1, lg: 1.5, xl: 1.75 },
    py: { md: 0.625, lg: 0.875 },
    borderRadius: 1.25,
    fontSize: { md: "0.75rem", lg: "0.8125rem", xl: "0.875rem" },
    fontWeight: active ? 600 : 500,
    textDecoration: "none",
    color: active ? "primary.main" : "text.secondary",
    bgcolor: active ? alpha(theme.palette.primary.main, 0.1) : "transparent",
    transition: "all 0.2s ease",
    whiteSpace: "nowrap",
    flexShrink: 0,
    "&:hover": {
      bgcolor: alpha(theme.palette.primary.main, 0.1),
      color: "primary.main",
    },
  });

export const iconButton: SxProps<Theme> = (theme) => ({
  width: 40,
  height: 40,
  borderRadius: 1.25,
  border: `1px solid ${theme.palette.divider}`,
  bgcolor: "background.paper",
  color: "text.secondary",
});

export const badge: SxProps<Theme> = {
  "& .MuiBadge-badge": {
    top: 8,
    insetInlineEnd: 9,
    width: 7,
    height: 7,
    minWidth: 7,
    border: "1.5px solid #fff",
  },
};

export const divider: SxProps<Theme> = {
  display: { xs: "none", sm: "block" },
  height: 28,
  alignSelf: "center",
  borderColor: "divider",
};

export const userPill: SxProps<Theme> = (theme) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: { xs: 0.5, sm: 0.75, md: 1, lg: 1.25 },
  py: { xs: 0.25, sm: 0.5, md: 0.625 },
  ps: { xs: 0.375, sm: 0.75, md: 1.25, lg: 1.5 },
  pe: { xs: 0.5, sm: 0.5, md: 0.625, lg: 0.75 },
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: { xs: 1.25, md: 1.5 },
  bgcolor: "background.paper",
  cursor: "pointer",
  fontFamily: theme.typography.fontFamily,
  flexShrink: 0,
  maxWidth: { xs: 96, sm: 120, md: "none" },
});

export const avatar: SxProps<Theme> = (theme) => ({
  width: { xs: 28, sm: 30, md: 32 },
  height: { xs: 28, sm: 30, md: 32 },
  borderRadius: 1.125,
  bgcolor: alpha(theme.palette.primary.main, 0.1),
  color: "primary.main",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 700,
  fontSize: { xs: "0.75rem", md: "0.875rem" },
  flexShrink: 0,
});

export const userText: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  lineHeight: 1.25,
  textAlign: "start",
  minWidth: 0,
  maxWidth: { xs: 48, sm: 60, md: 72, lg: 120, xl: 160 },
  overflow: "hidden",
};

export const userName: SxProps<Theme> = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontSize: { xs: "0.6875rem", sm: "0.75rem", lg: "0.8125rem" },
  fontWeight: 600,
  lineHeight: 1.2,
};

export const userRole: SxProps<Theme> = {
  display: { xs: "none", lg: "block" },
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontSize: "0.6875rem",
};

export const userChevron: SxProps<Theme> = {
  display: "flex",
  flexShrink: 0,
};

export const userMenuWrap: SxProps<Theme> = {
  position: "relative",
  flexShrink: 0,
};

export const menuBackdrop: SxProps<Theme> = {
  position: "fixed",
  inset: 0,
  zIndex: 19,
};
